import { useState, useCallback, useEffect } from 'react';
import {
  getProfile,
  updateProfile,
  getBalance,
  getBalanceHistory,
  getChildrenBalances,
  changeBranch,
  getBranches,
  getClinicFeatures,
  getChildren,
  getPets,
  addChild,
  addPet,
  updateChild,
  updatePet,
  deleteChild,
  deletePet,
  getAllProfileData,
  UserProfile,
  BalanceHistory,
  ChildBalance,
  Branch,
  ClinicFeatures,
  Child,
  Pet,
} from '../services/profileService';

export const useProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistory[]>([]);
  const [childrenBalances, setChildrenBalances] = useState<ChildBalance[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [features, setFeatures] = useState<ClinicFeatures>({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const loadSecondaryData = useCallback(async () => {
    setIsLoadingBalance(true);
    try {
      // Загружаем все данные одним запросом (быстрее чем 7 отдельных)
      const allData = await getAllProfileData();

      setBalance(allData.balances.parentBalance || 0);
      setBalanceHistory(allData.balanceHistory || []);
      setChildrenBalances(allData.balances.children || []);
      setChildren(allData.children || []);
      setPets(allData.pets || []);

      // Загружаем фичи отдельно
      const featuresData = await getClinicFeatures().catch(() => ({ features: {} }));
      setFeatures(featuresData.features || {});
    } catch (err: any) {
      setBalanceError(err.response?.data?.message || err.message || 'Ошибка загрузки данных');
    } finally {
      setIsLoadingBalance(false);
    }
  }, []);

  useEffect(() => {
    const loadCriticalData = async () => {
      setIsLoadingProfile(true);
      try {
        // Загружаем профиль и филиалы параллельно (критичные данные)
        const [profileData, branchesData] = await Promise.all([
          getProfile(),
          getBranches().catch(() => ({ branches: [], currentBranchId: '' })),
        ]);
        setProfile(profileData.user);
        setBranches(branchesData.branches || []);
      } catch (err: any) {
        setProfileError(err.response?.data?.message || err.message || 'Ошибка загрузки профиля');
      } finally {
        setIsLoadingProfile(false);
      }

      // Загружаем вторичные данные в фоне (не блокируем UI)
      loadSecondaryData();
    };

    loadCriticalData();
  }, [loadSecondaryData]);

  const handleUpdateProfile = useCallback(async (data: Partial<UserProfile>) => {
    try {
      const result = await updateProfile({ name: data.name });
      setProfile(result.user);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, []);

  const handleChangeBranch = useCallback(async (branchId: string) => {
    try {
      console.log('[handleChangeBranch] Изменение филиала на:', branchId);
      await changeBranch(branchId);
      console.log('[handleChangeBranch] Ответ сервера получен');
      if (profile) {
        console.log('[handleChangeBranch] Обновляю profile с новым branchId');
        setProfile({ ...profile, branchId });
      }
      return { success: true };
    } catch (error) {
      console.error('[handleChangeBranch] Ошибка:', error);
      return { success: false, error };
    }
  }, [profile]);

  const handleAddChild = useCallback(async (data: { name: string; birthDate: string; branchId?: string }) => {
    try {
      const result = await addChild(data);
      setChildren([...children, result.child]);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [children]);

  const handleUpdateChild = useCallback(async (childId: string, data: { name?: string; birthDate?: string; branchId?: string }) => {
    try {
      const result = await updateChild(childId, data);
      setChildren(children.map(c => c.id === childId ? result.child : c));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [children]);

  const handleDeleteChild = useCallback(async (childId: string) => {
    try {
      await deleteChild(childId);
      setChildren(children.filter(c => c.id !== childId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [children]);

  const handleAddPet = useCallback(async (data: { name: string; breed?: string; birthDate: string; branchId?: string }) => {
    try {
      const result = await addPet(data);
      setPets([...pets, result.pet]);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [pets]);

  const handleUpdatePet = useCallback(async (petId: string, data: { name?: string; breed?: string; birthDate?: string; branchId?: string }) => {
    try {
      const result = await updatePet(petId, data);
      setPets(pets.map(p => p.id === petId ? result.pet : p));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [pets]);

  const handleDeletePet = useCallback(async (petId: string) => {
    try {
      await deletePet(petId);
      setPets(pets.filter(p => p.id !== petId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }, [pets]);

  const refreshProfile = useCallback(async () => {
    try {
      const data = await getProfile();
      setProfile(data.user);
    } catch (err: any) {
      setProfileError(err.response?.data?.message || err.message || 'Ошибка загрузки профиля');
    }
  }, []);

  const refreshAllData = useCallback(async (includeProfile = true) => {
    try {
      if (includeProfile) {
        const [profileData, branchesData] = await Promise.all([
          getProfile(),
          getBranches().catch(() => ({ branches: [], currentBranchId: '' })),
        ]);
        setProfile(profileData.user);
        setBranches(branchesData.branches || []);
      }

      // Загружаем остальные данные в фоне
      await loadSecondaryData();
    } catch (err: any) {
      setProfileError(err.response?.data?.message || err.message || 'Ошибка загрузки данных');
    }
  }, [loadSecondaryData]);

  return {
    profile,
    balance,
    balanceHistory,
    childrenBalances,
    children,
    pets,
    branches,
    features,
    isLoadingProfile,
    isLoadingBalance,
    profileError,
    balanceError,
    handleUpdateProfile,
    handleChangeBranch,
    handleAddChild,
    handleUpdateChild,
    handleDeleteChild,
    handleAddPet,
    handleUpdatePet,
    handleDeletePet,
    refreshProfile,
    refreshAllData,
  };
};
